import fetch from 'isomorphic-fetch';
import { ApiError } from './Api';

const GDL_HEADERS = new Headers({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'App-Version': 2,
});

const ERROR_CODES = {
  ERR_NO_MODEL_DATA:
    'There is no model data available for this design.  This can occur if the design is very old. The design may be recoverable by contacting support.',
  ERR_CORRUPT_INPUT_FILE:
    'The model data for this file is corrupted and cannot be opened',
  ERR_BAD_WORD: 'Please choose a more appropriate name for your design',
  ERR_DB_ACCESS:
    'Error connecting to the database. Please reload the page and try again. If the problem persists, please contact support.',
  ERR_MODEL_FAILURE:
    'Sorry - there is a modeling error. Something about your design has resulted in an improper model. Please check your sketches and/or other solid modeling inputs.',
};

class GdlApi {
  constructor(config) {
    this.initialized = false;
    this.config = {
      endpoint: '/front',
      iid: '',
      branch: 'master',
    };

    if (typeof config === 'object') {
      this.setConfig(config);
    }
  }

  async handleResponse(response) {
    if (response.ok) {
      const data = await response.json();
      let rawErrors = [];
      if (data.errors && data.errors.length) {
        rawErrors = data.errors;
      } else if (data.message && data.message.startsWith('ERR_')) {
        rawErrors = [data.message];
      }
      if (rawErrors.length) {
        const errors = rawErrors.map((err) => {
          if (ERROR_CODES.hasOwnProperty(err)) {
            return ERROR_CODES[err];
          } else {
            return err;
          }
        });
        throw new ApiError(500, errors.join('\n'));
      } else {
        return data;
      }
    } else {
      fetch(`${this.config.endpoint}/log-snapshot`, {
        headers: {
          ...GDL_HEADERS,
          'x-gdliid': this.config.iid,
          'wb-gdl-branch': this.config.branch,
        },
        method: 'POST',
        body: JSON.stringify({
          status: response.status,
        }),
      });

      throw new ApiError(response.status, 'An error occurred');
    }
  }

  setConfig(config) {
    for (let key in this.config) {
      if (this.config.hasOwnProperty(key) && config.hasOwnProperty(key)) {
        this.config[key] = config[key];
      }
    }
  }

  logExceptionalCondition = async (message, params = {}, extra) => {
    const response = await fetch(
      `${this.config.endpoint}/bootstrap-ui/log-exceptional-condition`,
      {
        headers: {
          ...GDL_HEADERS,
          'x-gdliid': this.config.iid,
          'wb-gdl-branch': this.config.branch,
        },
        method: 'POST',
        body: JSON.stringify({
          message: message,
          params: params,
          gdlinitialized: this.initialized,
          gdlconfig: this.config,
          sessioniid: window.sessionStorage.getItem('gdl_id'),
          sessionappcode: window.sessionStorage.getItem('gdl_app'),
          ...extra,
        }),
      }
    );
  };

  genericRequest = async (path, nodeId, params = {}) => {
    if (!this.config.iid) {
      this.logExceptionalCondition(
        'genericRequest called with blank iid',
        params,
        { path, nodeId }
      );
    }

    const response = await fetch(
      `${this.config.endpoint}/bootstrap-ui/${path}`,
      {
        headers: {
          ...GDL_HEADERS,
          'x-gdliid': this.config.iid,
          'wb-gdl-branch': this.config.branch,
        },
        method: 'POST',
        body: JSON.stringify({
          'node-id': nodeId,
          ...params,
        }),
      }
    );
    return await this.handleResponse(response);
  };

  fileManagerRequest = async (action, params = {}) => {
    if (!this.config.iid) {
      this.logExceptionalCondition(
        'file-manager called with blank iid',
        params,
        { action }
      );
    }

    const response = await fetch(
      `${this.config.endpoint}/bootstrap-ui/file-manager`,
      {
        headers: {
          ...GDL_HEADERS,
          'x-gdliid': this.config.iid,
          'wb-gdl-branch': this.config.branch,
        },
        method: 'POST',
        body: JSON.stringify({
          action: action,
          ...params,
        }),
      }
    );
    return await this.handleResponse(response);
  };

  initializeSession = async (appCode, studentId, groupId, studentIdNumber) => {
    let iid = window.sessionStorage.getItem('gdl_id');
    let expectedAppCode = window.sessionStorage.getItem('gdl_app');
    let response, data;

    if (studentIdNumber === 1509816) {
      this.config.branch = 'devo';
      console.log('Requesting GDL devo branch');
    } else {
      this.config.branch = 'master';
    }

    // if we have a session id, try to resume it, but only if the app codes match
    if (iid && appCode === expectedAppCode) {
      try {
        response = await this.resumeSession(iid);
        data = await response.json();

        if (
          data.credentials.user_id !== studentId.toLowerCase() ||
          data.credentials.group_id !== groupId.toLowerCase()
        ) {
          this.initialized = false;
          data = null;
        }
      } catch (error) {
        // if (error.responseCode !== 400) {
        //   throw error;
        // }
        data = null;
      }
    }

    // we either did not have a session id or the session was expired.  try to start a new one
    if (!data || data.message === 'ERR_FAILED_SESSION') {
      try {
        response = await this.startSession(appCode, studentId, groupId);
        data = await response.json();
      } catch (error) {
        throw error;
      }
    }

    if (data && data.hasOwnProperty('session_id')) {
      this.setConfig({ iid: data.session_id });
      window.sessionStorage.setItem('gdl_id', data.session_id);
      window.sessionStorage.setItem('gdl_app', appCode);
    }

    return data;
  };

  /**
   * This should normally be called via the initialize method instead of being called directly
   *
   * @param appCode
   * @param studentId
   * @param groupId
   * @returns {Promise.<*>}
   */
  startSession = async (appCode, studentId, groupId) => {
    const response = await fetch(
      `${this.config.endpoint}/bootstrap-ui/start-application`,
      {
        headers: { ...GDL_HEADERS, 'wb-gdl-branch': this.config.branch },
        method: 'POST',
        body: JSON.stringify({
          'app-id': appCode,
          'user-id': studentId,
          'group-id': groupId,
          'appui-version': 2,
        }),
      }
    );
    if (response.ok) {
      this.initialized = true;
      return response;
    } else {
      throw new ApiError(response.status, response.statusText);
    }
  };

  /**
   * This should normally be called via the initialize method instead of being called directly
   *
   * @param iid
   * @returns {Promise.<*>}
   */
  resumeSession = async (iid) => {
    const response = await fetch(
      `${this.config.endpoint}/bootstrap-ui/resume-session`,
      {
        headers: {
          ...GDL_HEADERS,
          'X-GDLIID': iid,
          'wb-gdl-branch': this.config.branch,
        },
        method: 'POST',
      }
    );
    if (response.ok) {
      this.initialized = true;
      return response;
    } else {
      throw new ApiError(response.status, response.statusText);
    }
  };

  quitSession = async () => {
    window.sessionStorage.removeItem('gdl_id');
    if (this.config.iid) {
      const response = await fetch(
        `${this.config.endpoint}/bootstrap-ui/logout`,
        {
          headers: {
            ...GDL_HEADERS,
            'X-GDLIID': this.config.iid,
            'wb-gdl-branch': this.config.branch,
          },
          method: 'POST',
        }
      );
      this.config.iid = '';
      this.initialized = false;
      return response;
    } else {
      return Promise.resolve();
    }
  };

  /**
   * Reset GDL to a clean session.
   * Called directly (from action promise) when leveling up in Game Mode.
   */
  resetSession = async () => {
    const response = await fetch(
      `${this.config.endpoint}/bootstrap-ui/reload-session`,
      {
        headers: {
          ...GDL_HEADERS,
          'X-GDLIID': this.config.iid,
          'wb-gdl-branch': this.config.branch,
        },
        method: 'POST',
      }
    );

    if (response.ok) {
      return await response.json();
    } else {
      throw new ApiError(response.status, response.statusText);
    }
  };

  newConcept = () => this.fileManagerRequest('new');

  saveRevision = (compete, notes, name) =>
    this.fileManagerRequest('save', { compete, notes, name });

  openRevision = (id, name, studentId, groupId) =>
    this.fileManagerRequest('open', { id, name, studentId, groupId });

  openWorkArea = (nodeId) => this.genericRequest('open-work-area', nodeId);

  closeWorkArea = (nodeId) => this.genericRequest('close-work-area', nodeId);

  submitWorkArea = (nodeId, inputs) =>
    this.genericRequest('set-work-area-inputs', nodeId, inputs);

  resetWorkArea = (nodeId) =>
    this.genericRequest('reset-work-area-inputs', nodeId);

  toggleGraphics = (nodeId) => this.genericRequest('toggle-graphics', nodeId);

  clearGraphics = () => this.genericRequest('clear-all-graphics');

  syncGraphics = () => this.genericRequest('sync-graphics');

  updateSketcher = (nodeId, params) =>
    this.genericRequest('update-sketch', nodeId, params);

  startSimulation = async (design1, design2, skipUnity) => {
    if (!this.config.iid) {
      this.logExceptionalCondition(
        'startSimulation called with blank iid',
        {},
        { design1, design2 }
      );
    }

    const response = await fetch(
      `${this.config.endpoint}/bootstrap-ui/start-simulation`,
      {
        headers: {
          ...GDL_HEADERS,
          'x-gdliid': this.config.iid,
          'wb-gdl-branch': this.config.branch,
        },
        method: 'POST',
        body: JSON.stringify({
          'design1-id': design1,
          'design2-id': design2,
        }),
      }
    );
    let data = await this.handleResponse(response);
    if (skipUnity) {
      data.skipUnity = true;
    }
    return data; 
  };
}

export default GdlApi;
